import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  Award,
  Briefcase,
  Building2,
  Camera,
  CheckCircle2,
  Crown,
  IdCard,
  Loader2,
  Lock,
  Mail,
  Phone,
  Shield,
  Star,
  TrendingUp,
  User,
  Users2,
  Zap,
} from 'lucide-react';
import type { AuthorityLevel } from '../../../components/UserAuthorityBadge';
import { useGamification } from '../../../hooks/useGamification';
import { supabase } from '../../../lib/supabaseClient';
import { useAuth } from '../../../context/AuthContext';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { useAppToast } from '../../../components/AppToast';
import {
  ModuleErrorState,
  ModuleLoadingState,
} from '../../shared/components/ModulePageHeader';

type ProfileData = {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  position?: string | null;
  company?: string | null;
  department?: string | null;
  avatar_url?: string | null;
  updated_at?: string | null;
};

type EditableField = 'name' | 'phone' | 'position' | 'company' | 'department';
type FieldStatus = 'idle' | 'saving' | 'saved' | 'error';

const ROLE_LABELS: Record<string, string> = {
  consultor_master: 'Consultor Master',
  consultant: 'Consultor',
  admin: 'Admin',
  manager: 'Gestor',
  gestor: 'Gestor',
  lead: 'Coordenador',
  coordenador: 'Coordenador',
  operator: 'Usuário',
  usuario: 'Usuário',
};

const BELT_THRESHOLDS: Record<AuthorityLevel, { label: string; min: number; next: number | null; icon: React.ElementType; tone: string; ring: string }> = {
  bronze: { label: 'Bronze Belt', min: 0, next: 1000, icon: Award, tone: 'text-orange-700 dark:text-orange-400', ring: 'ring-orange-500/60' },
  silver: { label: 'Silver Belt', min: 1000, next: 5000, icon: TrendingUp, tone: 'text-slate-600 dark:text-slate-300', ring: 'ring-slate-400/60' },
  gold: { label: 'Gold Belt', min: 5000, next: 15000, icon: Zap, tone: 'text-amber-600 dark:text-amber-400', ring: 'ring-amber-500/60' },
  black_belt: { label: 'Black Belt', min: 15000, next: null, icon: Crown, tone: 'text-purple-700 dark:text-purple-400', ring: 'ring-purple-500/60' },
};

function resolveAuthorityProgress(level: AuthorityLevel, points: number) {
  const cfg = BELT_THRESHOLDS[level];
  if (cfg.next === null) return { percent: 100, remaining: 0 };
  const span = cfg.next - cfg.min;
  const earned = Math.max(0, points - cfg.min);
  const percent = span > 0 ? Math.min(100, Math.max(0, (earned / span) * 100)) : 0;
  const remaining = Math.max(0, cfg.next - points);
  return { percent, remaining };
}

export const AccountProfilePage: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const { showToast, ToastContainer } = useAppToast();
  const { userPoints, loading: gamLoading, error: gamError } = useGamification();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  const initialRef = useRef<Record<EditableField, string>>({
    name: '',
    phone: '',
    position: '',
    company: '',
    department: '',
  });

  const [draft, setDraft] = useState<Record<EditableField, string>>({
    name: '',
    phone: '',
    position: '',
    company: '',
    department: '',
  });
  const [status, setStatus] = useState<Record<EditableField, FieldStatus>>({
    name: 'idle',
    phone: 'idle',
    position: 'idle',
    company: 'idle',
    department: 'idle',
  });

  const profileQuery = useQuery({
    queryKey: ['account-profile', user?.id],
    enabled: Boolean(user?.id),
    queryFn: async (): Promise<ProfileData | null> => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('user_profiles')
        .select('name, email, phone, position, company, department, avatar_url, updated_at')
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    const data = profileQuery.data;
    const next: Record<EditableField, string> = {
      name: data?.name ?? user?.name ?? '',
      phone: data?.phone ?? '',
      position: data?.position ?? '',
      company: data?.company ?? '',
      department: data?.department ?? '',
    };
    setDraft(next);
    initialRef.current = { ...next };
    setPreviewImage(data?.avatar_url ?? '');
  }, [profileQuery.data, user?.name]);

  const emailValue = useMemo(
    () => profileQuery.data?.email || user?.email || '',
    [profileQuery.data?.email, user?.email],
  );

  const roleLabel = useMemo(() => {
    const role = (workspace?.userRole || user?.role || '').toString();
    return ROLE_LABELS[role] || 'Usuário';
  }, [workspace?.userRole, user?.role]);

  const hasGamificationRecord = Boolean(userPoints);
  const authorityLevel: AuthorityLevel = userPoints?.authority_level ?? 'bronze';
  const totalPoints = userPoints?.total_points ?? 0;
  const belt = BELT_THRESHOLDS[authorityLevel];
  const BeltIcon = belt.icon;
  const progress = resolveAuthorityProgress(authorityLevel, totalPoints);

  const resizeImageToDataUrl = (file: File, maxSize = 256, quality = 0.85): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Falha ao ler imagem'));
      reader.onload = () => {
        const img = new Image();
        img.onerror = () => reject(new Error('Imagem inválida'));
        img.onload = () => {
          const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
          const w = Math.max(1, Math.round(img.width * scale));
          const h = Math.max(1, Math.round(img.height * scale));
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (!ctx) return reject(new Error('Canvas indisponível'));
          ctx.drawImage(img, 0, 0, w, h);
          try {
            resolve(canvas.toDataURL('image/jpeg', quality));
          } catch (e) {
            reject(e);
          }
        };
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    });

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id) return;

    if (!file.type.startsWith('image/')) {
      showToast('warning', 'Selecione um arquivo de imagem.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('warning', 'Imagem muito grande (máx. 5MB).');
      return;
    }

    setAvatarUploading(true);
    try {
      const dataUrl = await resizeImageToDataUrl(file, 256, 0.85);
      setPreviewImage(dataUrl);

      const { error: upsertError } = await supabase
        .from('user_profiles')
        .upsert(
          { user_id: user.id, avatar_url: dataUrl, updated_at: new Date().toISOString() },
          { onConflict: 'user_id' },
        );
      if (upsertError) throw upsertError;

      await supabase.auth.updateUser({ data: { avatar_url: dataUrl } });
      await queryClient.invalidateQueries({ queryKey: ['account-profile', user.id] });
      showToast('success', 'Foto de perfil atualizada.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Falha ao atualizar a foto.';
      console.error('[AccountProfile] upload avatar:', error);
      showToast('error', message);
    } finally {
      setAvatarUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const saveField = async (field: EditableField) => {
    if (!user?.id) return;
    const value = (draft[field] || '').trim();
    if (value === initialRef.current[field]) return;

    setStatus((s) => ({ ...s, [field]: 'saving' }));
    try {
      const payload: Record<string, unknown> = {
        user_id: user.id,
        [field]: value || null,
        updated_at: new Date().toISOString(),
      };
      const { error } = await supabase
        .from('user_profiles')
        .upsert(payload, { onConflict: 'user_id' });
      if (error) throw error;

      if (field === 'name') {
        await supabase.auth.updateUser({ data: { name: value } });
      }

      initialRef.current[field] = value;
      setStatus((s) => ({ ...s, [field]: 'saved' }));
      window.setTimeout(() => {
        setStatus((s) => (s[field] === 'saved' ? { ...s, [field]: 'idle' } : s));
      }, 1800);

      queryClient.setQueryData<ProfileData | null>(
        ['account-profile', user.id],
        (prev) => ({ ...(prev || {}), [field]: value, updated_at: new Date().toISOString() }),
      );
    } catch (error) {
      console.error(`[AccountProfile] saveField(${field}):`, error);
      setStatus((s) => ({ ...s, [field]: 'error' }));
      showToast('error', `Não foi possível salvar ${field}.`);
    }
  };

  const updateDraft = (field: EditableField, value: string) => {
    setDraft((d) => ({ ...d, [field]: value }));
  };

  if (profileQuery.isPending) {
    return (
      <div className="enterprise-page h-full p-6">
        <ModuleLoadingState label="Carregando perfil" />
      </div>
    );
  }

  if (profileQuery.isError) {
    return (
      <div className="enterprise-page h-full p-6">
        <ModuleErrorState
          message="Não foi possível carregar o perfil do usuário no momento."
          retry={profileQuery.refetch}
        />
      </div>
    );
  }

  return (
    <div className="enterprise-page flex h-full min-h-0 w-full flex-col overflow-y-auto !gap-0 !p-0 bg-slate-100/70 dark:bg-[var(--bg-workspace)]">
      <div className="grid w-full flex-1 grid-cols-1 items-stretch gap-4 p-4 md:gap-5 md:p-5 lg:min-h-0 lg:grid-cols-[minmax(280px,35%)_minmax(0,65%)] lg:p-6">
        {/* ── Coluna da Autoridade (35%) ───────────────────────── */}
        <aside className="flex shrink-0 flex-col rounded-sm border border-slate-200/80 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] md:p-6 dark:border-[var(--border-default)] dark:bg-[var(--bg-elevated)] lg:min-h-0">
            <p className="mb-5 text-center text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 dark:text-[var(--text-muted)]">
              Painel de Autoridade
            </p>

            <div className="flex flex-col items-center text-center">
              <div className={`relative group mb-4 rounded-full p-1 ring-2 ${belt.ring}`}>
                <div className="relative h-28 w-28 overflow-hidden rounded-full border border-slate-100 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
                  {previewImage ? (
                    <img src={previewImage} alt="Avatar" className="h-full w-full object-cover" />
                  ) : (
                    <span className="flex h-full w-full items-center justify-center text-3xl font-semibold text-slate-400 dark:text-slate-500">
                      {(draft.name || user?.email || 'US').substring(0, 2).toUpperCase()}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={avatarUploading}
                    title="Alterar foto"
                    className="absolute inset-0 flex items-center justify-center bg-slate-900/45 text-white opacity-0 transition-opacity group-hover:opacity-100 disabled:cursor-wait disabled:opacity-100"
                  >
                    {avatarUploading ? <Loader2 size={20} className="animate-spin" /> : <Camera size={20} />}
                  </button>
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
              </div>

              <h2 className="max-w-full truncate text-lg font-bold text-slate-900 dark:text-[var(--text-primary)]">
                {draft.name || user?.name || 'Usuário'}
              </h2>
              <p className="mt-0.5 max-w-full truncate text-[11px] text-slate-500 dark:text-[var(--text-muted)]">
                {emailValue}
              </p>
              <span className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-blue-100 bg-blue-50 px-2.5 py-0.5 text-[10px] font-semibold text-blue-700 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                <Shield size={10} strokeWidth={2} />
                {roleLabel}
              </span>
            </div>

            <div className="mt-6 rounded-sm border border-slate-100 bg-slate-50/80 p-4 dark:border-[var(--border-default)] dark:bg-[var(--bg-surface)]">
              <div className="mb-3 flex items-center justify-center gap-2">
                <BeltIcon size={18} className={belt.tone} />
                <span className={`text-[13px] font-bold tracking-wide ${belt.tone}`}>{belt.label}</span>
              </div>

              {gamLoading ? (
                <div className="h-3 animate-pulse rounded-full bg-slate-200 dark:bg-slate-700" />
              ) : !hasGamificationRecord ? (
                <div className="space-y-2 text-center">
                  <p className="text-[11px] font-medium text-slate-600 dark:text-[var(--text-secondary)]">
                    Jornada iniciando
                  </p>
                  <div className="h-2.5 overflow-hidden rounded-full bg-slate-200/80 dark:bg-slate-700">
                    <div className="h-full w-[4%] rounded-full bg-gradient-to-r from-slate-400 to-slate-500" />
                  </div>
                  <p className="text-[10px] text-slate-400 dark:text-[var(--text-muted)]">
                    {gamError
                      ? 'Não foi possível carregar a pontuação agora.'
                      : '0 pts — aguardando primeiros registros no ambiente.'}
                  </p>
                </div>
              ) : authorityLevel === 'black_belt' ? (
                <p className="text-center text-[11px] font-semibold text-purple-700 dark:text-purple-400">
                  Nível máximo atingido
                </p>
              ) : (
                <>
                  <div className="mb-2 flex items-center justify-between text-[10px] text-slate-500 dark:text-[var(--text-muted)]">
                    <span>Progresso de pontuação</span>
                    <span className="font-semibold tabular-nums text-slate-700 dark:text-[var(--text-secondary)]">
                      {Math.round(progress.percent)}%
                    </span>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-slate-200/80 dark:bg-slate-700">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-slate-700 via-blue-600 to-emerald-500 transition-all duration-500"
                      style={{ width: `${Math.max(4, progress.percent)}%` }}
                    />
                  </div>
                  <p className="mt-2 text-center text-[10px] tabular-nums text-slate-500 dark:text-[var(--text-muted)]">
                    <span className="font-semibold text-slate-700 dark:text-[var(--text-secondary)]">
                      {progress.remaining.toLocaleString('pt-BR')}
                    </span>{' '}
                    pts para o próximo nível
                  </p>
                  <p className="mt-1 text-center text-[11px] font-bold tabular-nums text-slate-800 dark:text-[var(--text-primary)]">
                    {totalPoints.toLocaleString('pt-BR')} pts acumulados
                  </p>
                </>
              )}
            </div>

            {!gamLoading && hasGamificationRecord && userPoints && (
              <div className="mt-auto grid grid-cols-2 gap-2 pt-4">
                <AuthorityStat icon={<Star size={12} className="text-amber-500" />} label="Kaizens" value={String(userPoints.kaizens_completed)} />
                <AuthorityStat icon={<Zap size={12} className="text-blue-500" />} label="Projetos" value={String(userPoints.projects_led)} />
                <AuthorityStat icon={<Activity size={12} className="text-emerald-500" />} label="Disciplina" value={userPoints.discipline_points.toLocaleString('pt-BR')} />
                <AuthorityStat icon={<Users2 size={12} className="text-violet-500" />} label="Colaboração" value={String(userPoints.team_collaborations)} />
              </div>
            )}
        </aside>

        {/* ── Prancheta de Dados (65%) ─────────────────────────── */}
        <section className="flex shrink-0 flex-col rounded-sm border border-slate-200/80 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] md:p-6 dark:border-[var(--border-default)] dark:bg-[var(--bg-elevated)] lg:min-h-0">
          <p className="mb-5 shrink-0 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 dark:text-[var(--text-muted)]">
            Prancheta de Dados
          </p>

          <div className="flex w-full max-w-none flex-1 flex-col lg:min-h-0">
            <div className="w-full flex-1 space-y-6">
              <DataBlock title="Identidade">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <ProfileField
                      label="Nome Completo"
                      icon={<User size={14} />}
                      value={draft.name}
                      placeholder="Seu nome completo"
                      status={status.name}
                      onChange={(v) => updateDraft('name', v)}
                      onBlur={() => saveField('name')}
                    />
                  </div>
                </div>
              </DataBlock>

              <DataBlock title="Corporativo">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <ProfileField
                    label="Empresa"
                    icon={<Building2 size={14} />}
                    value={draft.company}
                    placeholder="Nome da empresa"
                    status={status.company}
                    onChange={(v) => updateDraft('company', v)}
                    onBlur={() => saveField('company')}
                  />
                  <ProfileField
                    label="Departamento"
                    icon={<Building2 size={14} />}
                    value={draft.department}
                    placeholder="Ex: Excelência Operacional"
                    status={status.department}
                    onChange={(v) => updateDraft('department', v)}
                    onBlur={() => saveField('department')}
                  />
                  <ProfileField
                    label="Cargo"
                    icon={<Briefcase size={14} />}
                    value={draft.position}
                    placeholder="Ex: Consultor Lean"
                    status={status.position}
                    onChange={(v) => updateDraft('position', v)}
                    onBlur={() => saveField('position')}
                  />
                </div>
              </DataBlock>

              <DataBlock title="Contato">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <ProfileField
                    label="Telefone"
                    icon={<Phone size={14} />}
                    value={draft.phone}
                    placeholder="(11) 99999-9999"
                    status={status.phone}
                    onChange={(v) => updateDraft('phone', v)}
                    onBlur={() => saveField('phone')}
                  />
                  <ProfileField
                    label="E-mail"
                    icon={<Mail size={14} />}
                    value={emailValue}
                    placeholder="email@empresa.com"
                    readOnly
                    type="email"
                  />
                  <ProfileField
                    label="CPF"
                    icon={<IdCard size={14} />}
                    value=""
                    placeholder="Não cadastrado"
                    readOnly
                  />
                </div>
              </DataBlock>
            </div>

            <p className="mt-auto shrink-0 border-t border-slate-100 pt-4 text-[11px] text-slate-400/80 dark:border-[var(--border-default)] dark:text-[var(--text-muted)]">
              As alterações são salvas automaticamente ao sair do campo.
            </p>
          </div>
        </section>
      </div>

      <ToastContainer />
    </div>
  );
};

function DataBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-3 border-b border-slate-100 pb-2 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 dark:border-[var(--border-default)] dark:text-[var(--text-muted)]">
        {title}
      </h3>
      {children}
    </div>
  );
}

function AuthorityStat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-sm border border-slate-100 bg-white px-2.5 py-2 dark:border-[var(--border-default)] dark:bg-[var(--bg-elevated)]">
      <div className="flex items-center gap-1.5">
        {icon}
        <span className="text-[9px] font-bold uppercase tracking-[0.08em] text-slate-400 dark:text-[var(--text-muted)]">
          {label}
        </span>
      </div>
      <p className="mt-1 text-[13px] font-bold tabular-nums text-slate-800 dark:text-[var(--text-primary)]">{value}</p>
    </div>
  );
}

interface ProfileFieldProps {
  label: string;
  icon: React.ReactNode;
  value: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  status?: FieldStatus;
  readOnly?: boolean;
  type?: string;
}

const ProfileField: React.FC<ProfileFieldProps> = ({
  label,
  icon,
  value,
  onChange,
  onBlur,
  placeholder,
  status = 'idle',
  readOnly = false,
  type = 'text',
}) => (
  <div>
    <div className="mb-1.5 flex items-center justify-between">
      <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500 dark:text-[var(--text-muted)]">
        {label}
      </label>
      {status === 'saving' && (
        <span className="flex items-center gap-1 text-[10px] font-medium text-slate-400">
          <Loader2 size={10} className="animate-spin" /> Salvando…
        </span>
      )}
      {status === 'saved' && (
        <span className="flex items-center gap-1 text-[10px] font-medium text-emerald-600">
          <CheckCircle2 size={10} /> Salvo
        </span>
      )}
      {status === 'error' && (
        <span className="text-[10px] font-medium text-rose-500">Falha ao salvar</span>
      )}
    </div>
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500">{icon}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        readOnly={readOnly}
        disabled={readOnly}
        className={`h-10 w-full rounded-sm border pl-9 text-sm transition-colors ${
          readOnly
            ? 'cursor-default border-slate-200 bg-slate-50 pr-10 text-slate-700 dark:border-[var(--border-default)] dark:bg-slate-800/60 dark:text-[var(--text-primary)]'
            : 'border-slate-200 bg-white pr-3 text-slate-800 focus:border-slate-400 focus:outline-none dark:border-[var(--border-default)] dark:bg-[var(--bg-surface)] dark:text-[var(--text-primary)]'
        }`}
      />
      {readOnly && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500">
          <Lock size={13} strokeWidth={1.75} />
        </span>
      )}
    </div>
  </div>
);

export default AccountProfilePage;
